(function() {
  'use strict';

  const ShoppableArticle = {
    init: function() {
      this.bindEvents();
      this.wireInlineProducts();
      this.autoInsertInlineProducts();
    },

    bindEvents: function() {
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('.sa-atc-btn, .pc-atc[data-variant-id]');
        if (btn) {
          e.preventDefault();
          this.handleAddToCart(btn);
        }
      });
    },

    getProducts: function() {
      if (this._products) return this._products;
      const el = document.querySelector('.sa-products-data');
      if (!el) {
        this._products = [];
        return this._products;
      }
      try {
        this._products = JSON.parse(el.textContent) || [];
      } catch (error) {
        console.error('Shoppable article: invalid product data', error);
        this._products = [];
      }
      return this._products;
    },

    // Author places empty <div class="sa-inline-product" data-product-handle="..."></div>
    // (or data-product-index="0") markers in the article body; we render them from the
    // same custom.blog_recommended_products metafield used by the rail and grid. Existing
    // hand-authored .sa-inline-product[data-variant-id] cards keep their content and get
    // a legacy add-to-cart button appended.
    wireInlineProducts: function() {
      const products = this.getProducts();

      document.querySelectorAll('.sa-inline-product').forEach((card) => {
        if (card.querySelector('.pc-atc, .sa-inline-product__atc, .sa-atc-btn')) return;

        let product = null;
        const handle = card.getAttribute('data-product-handle');
        const index = card.getAttribute('data-product-index');
        const variantId = card.getAttribute('data-variant-id');
        const hasProductMarker = Boolean(handle) || index !== null;

        if (handle) {
          product = products.find((p) => p.handle === handle);
        } else if (index !== null) {
          product = products[parseInt(index, 10)];
        }

        if (product) {
          this.renderInlineCard(card, product);
          return;
        }

        if (variantId) {
          this.appendLegacyInlineButton(card, variantId);
          return;
        }

        if (hasProductMarker && this.isEmptyInlineMarker(card)) {
          card.style.display = 'none';
        }
      });
    },

    autoInsertInlineProducts: function() {
      const articleBody = document.querySelector('.sa-article-body');
      if (!articleBody) return;

      const hasInlineCards = Array.from(articleBody.querySelectorAll('.sa-inline-product, .product-card'))
        .some((card) => !card.closest('.sa-shop-grid'));
      if (hasInlineCards) return;

      const products = this.getProducts().filter((product) => product && product.variant_id);
      const cardCount = Math.min(2, products.length);
      if (!cardCount) return;

      const insertionPoints = this.getInlineInsertionPoints(articleBody, cardCount);
      insertionPoints.forEach((point, index) => {
        const card = document.createElement('div');
        card.className = 'product-card';
        card.setAttribute('data-auto-inline-product', 'true');
        this.renderInlineCard(card, products[index]);
        point.insertAdjacentElement('afterend', card);
      });
    },

    getInlineInsertionPoints: function(articleBody, count) {
      const contentChildren = [];

      Array.from(articleBody.children).some((child) => {
        if (child.classList.contains('sa-shop-grid')) return true;
        contentChildren.push(child);
        return false;
      });

      const strongParagraphs = contentChildren.filter((child) => this.isMeaningfulParagraph(child, 70));
      const fallbackParagraphs = contentChildren.filter((child) => this.isMeaningfulParagraph(child, 25));
      const paragraphs = strongParagraphs.length >= count ? strongParagraphs : fallbackParagraphs;
      const desiredCount = Math.min(count, paragraphs.length);

      if (!desiredCount) return [];
      if (desiredCount === 1) {
        return [paragraphs[this.getNearestUnusedIndex(paragraphs, 0.45, [])]];
      }

      const usedIndexes = [];
      [0.35, 0.65].slice(0, desiredCount).forEach((fraction) => {
        usedIndexes.push(this.getNearestUnusedIndex(paragraphs, fraction, usedIndexes));
      });

      return usedIndexes
        .sort((a, b) => a - b)
        .map((index) => paragraphs[index]);
    },

    getNearestUnusedIndex: function(items, fraction, usedIndexes) {
      const maxIndex = items.length - 1;
      let targetIndex = Math.round(maxIndex * fraction);

      if (items.length > 2 && targetIndex === 0) targetIndex = 1;
      if (items.length > 3 && targetIndex === maxIndex) targetIndex = maxIndex - 1;

      for (let offset = 0; offset <= maxIndex; offset += 1) {
        const forward = targetIndex + offset;
        if (forward <= maxIndex && !usedIndexes.includes(forward)) return forward;

        const backward = targetIndex - offset;
        if (backward >= 0 && !usedIndexes.includes(backward)) return backward;
      }

      return targetIndex;
    },

    isMeaningfulParagraph: function(element, minLength) {
      if (!element || element.tagName !== 'P') return false;
      if (element.closest('.sa-shop-grid')) return false;

      const text = (element.textContent || '').replace(/\s+/g, ' ').trim();
      if (text.length < minLength) return false;

      const hasMedia = Boolean(element.querySelector('img, picture, svg, iframe, video'));
      return !hasMedia || text.length >= 120;
    },

    isEmptyInlineMarker: function(card) {
      return card.textContent.trim() === '' && card.children.length === 0;
    },

    appendLegacyInlineButton: function(card, variantId) {
      const productName = card.querySelector('.sa-inline-product__name, .pc-name')?.textContent?.trim() || 'Product';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'sa-inline-product__atc sa-atc-btn';
      btn.setAttribute('data-variant-id', variantId);
      btn.setAttribute('data-product-name', productName);
      btn.setAttribute('aria-label', `Add ${productName} to cart`);
      btn.textContent = 'Add to cart →';
      card.appendChild(btn);
    },

    renderInlineCard: function(card, product) {
      card.innerHTML = '';
      card.classList.remove('sa-inline-product');
      card.classList.add('product-card');

      const imageWrap = document.createElement('div');
      imageWrap.className = 'pc-img';
      if (product.image) {
        const img = document.createElement('img');
        img.src = product.image;
        img.alt = product.title;
        img.loading = 'lazy';
        img.width = 80;
        img.height = 80;
        imageWrap.appendChild(img);
      }

      const info = document.createElement('div');
      info.className = 'pc-info';

      const name = document.createElement('p');
      name.className = 'pc-name';
      name.textContent = product.title;
      info.appendChild(name);

      const price = document.createElement('p');
      price.className = 'pc-price';
      price.appendChild(document.createTextNode(product.price));
      if (product.subtitle) {
        price.appendChild(document.createTextNode(' '));
        const count = document.createElement('span');
        count.className = 'pc-count';
        count.textContent = `· ${product.subtitle}`;
        price.appendChild(count);
      }
      info.appendChild(price);

      const ratingCount = Number(product.rating_count) || 0;
      if (ratingCount > 0) {
        const stars = document.createElement('p');
        stars.className = 'pc-stars';
        const filled = Math.max(0, Math.min(5, Math.round(product.rating || 0)));
        const starSpan = document.createElement('span');
        starSpan.textContent = '★★★★★'.slice(0, filled) + '☆☆☆☆☆'.slice(0, 5 - filled);
        stars.appendChild(starSpan);
        stars.appendChild(
          document.createTextNode(' \u00a0' + ratingCount.toLocaleString() + ' reviews')
        );
        info.appendChild(stars);
      }

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pc-atc';
      btn.setAttribute('data-variant-id', product.variant_id);
      btn.setAttribute('data-product-name', product.title);
      btn.setAttribute('aria-label', `Add ${product.title} to cart`);
      btn.textContent = 'Add to cart →';

      card.appendChild(imageWrap);
      card.appendChild(info);
      card.appendChild(btn);
    },

    getCartAddJsonUrl: function() {
      const cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
      const queryIndex = cartAddUrl.indexOf('?');
      const path = queryIndex === -1 ? cartAddUrl : cartAddUrl.slice(0, queryIndex);
      const query = queryIndex === -1 ? '' : cartAddUrl.slice(queryIndex);

      if (/\.js\/?$/.test(path)) {
        return path.replace(/\/$/, '') + query;
      }

      if (/\/cart\/add\/?$/.test(path)) {
        return path.replace(/\/?$/, '.js') + query;
      }

      return cartAddUrl;
    },

    handleAddToCart: function(button) {
      const variantId = button.getAttribute('data-variant-id');
      if (!variantId) return;

      button.setAttribute('aria-busy', 'true');
      const originalText = button.textContent;

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const sections = cart && typeof cart.getSectionsToRender === 'function'
        ? cart.getSectionsToRender()
        : [{ id: 'cart-icon-bubble' }];

      if (sections.length) {
        formData.append('sections', sections.map((section) => section.id).join(','));
        formData.append('sections_url', window.location.pathname);
      }

      if (cart && typeof cart.setActiveElement === 'function') {
        cart.setActiveElement(button);
      }

      fetch(this.getCartAddJsonUrl(), {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      })
        .then((response) => {
          const contentType = (response.headers.get('content-type') || '').toLowerCase();
          if (!contentType.includes('json') && !contentType.includes('javascript')) {
            throw new Error(`Expected JSON cart response, received ${contentType || 'unknown content type'}`);
          }
          return response.json();
        })
        .then((response) => {
          if (response.status) {
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'shoppable-article',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message
              });
            }
            return;
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'shoppable-article',
              productVariantId: variantId,
              cartData: response
            });
          }

          if (cart && typeof cart.renderContents === 'function') {
            cart.renderContents(response);
          } else {
            this.renderSections(response, sections);
          }

          const productName = button.getAttribute('data-product-name') || 'Item';
          this.showToast(`${productName} added to cart ✓`);
        })
        .catch((error) => {
          console.error('Shoppable article ATC error:', error);
        })
        .finally(() => {
          button.setAttribute('aria-busy', 'false');
        });
    },

    renderSections: function(response, sections) {
      if (!response.sections) return;

      sections.forEach((section) => {
        const target = document.getElementById(section.id);
        const sectionHtml = response.sections[section.id];
        if (!target || !sectionHtml) return;

        const selector = section.selector || '.shopify-section';
        const source = new DOMParser()
          .parseFromString(sectionHtml, 'text/html')
          .querySelector(selector);
        if (source) {
          target.innerHTML = source.innerHTML;
        }
      });
    },

    showToast: function(message) {
      const toast = document.querySelector('.sa-toast');
      if (!toast) return;

      const msgEl = toast.querySelector('.sa-toast-message');
      if (msgEl) {
        msgEl.textContent = message;
      }

      toast.classList.add('sa-show');

      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        toast.classList.remove('sa-show');
      }, 2800);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      ShoppableArticle.init();
    });
  } else {
    ShoppableArticle.init();
  }
})();
